import * as React from 'react';
import { FC, useMemo } from 'react';

import { ScrollView } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import { Box, Typography } from 'components/ui';
import { useIsDesktopBreakpoint, useIsLowHeightDevice } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { primitive, spacing } from 'styles/constants';
import { WEB_APP_CTA_MAX_WIDTH } from 'utils/constants';
import { isWeb } from 'utils/environment';

import { DEFAULT_CONTENT, EMPTY_STATES_CONTENT } from './constants';
import { EmptyStateAction } from './empty-state-action';
import { EmptyStateCategories, IStoreLocatorEmptyStatesProps } from './types';

function tabCaresAboutAuthStatus(tab: OrderLocatorTab): boolean {
  if (tab === OrderLocatorTab.FAVORITE || tab === OrderLocatorTab.RECENT) {
    return true;
  }
  return false;
}
function tabCaresAboutLocationStatus(tab: OrderLocatorTab): boolean {
  if (tab === OrderLocatorTab.NEARBY) {
    return true;
  }
  return false;
}

/**
 * determineEmptyStateCategory
 *  logic for prioritization & determination
 *  of empty state category for specified tab
 *  Priority Levels:
 *    1. An error occurreed
 *    2. Unauthenticated
 *    3. Location Permissions Not Granted
 *    4. No Stores Available
 */
function determineEmptyStateCategory({
  tab,
  isAuthenticated,
  locationPermissionsGranted,
  currentTabError,
  coordinatesAvailable,
}: {
  tab: OrderLocatorTab;
  isAuthenticated: boolean;
  locationPermissionsGranted: boolean;
  currentTabError: boolean;
  coordinatesAvailable?: boolean;
}): EmptyStateCategories {
  let category = EmptyStateCategories.NO_STORES;
  if (currentTabError) {
    category = EmptyStateCategories.ERROR;
  } else if (tabCaresAboutAuthStatus(tab) && !isAuthenticated) {
    category = EmptyStateCategories.NO_AUTH;
  } else if (
    tabCaresAboutLocationStatus(tab) &&
    // if we have manual coords, we don't want the empty state to be no location
    !coordinatesAvailable &&
    !locationPermissionsGranted
  ) {
    category = EmptyStateCategories.NO_LOCATION;
  }
  return category;
}

/**
 *
 * StoreLocatorEmptyStates enables displaying different empty state messages for store locator tabs
 *
 */
export const StoreLocatorEmptyStates: FC<React.PropsWithChildren<
  IStoreLocatorEmptyStatesProps
>> = ({
  tab,
  locationPermissionsGranted,
  currentTabError,
  coordinatesAvailable,
  wrapperStyle,
  titleOverride,
  fillAndCenter = false,
}) => {
  const { isAuthenticated } = useAuthContext();
  const isLowHeightDevice = useIsLowHeightDevice();
  const isDesktop = useIsDesktopBreakpoint();

  const { icon: Icon, titleId, action } = useMemo(() => {
    if (titleOverride) {
      return (
        EMPTY_STATES_CONTENT[OrderLocatorTab.NEARBY]?.[EmptyStateCategories.NO_STORES] ||
        DEFAULT_CONTENT
      );
    }
    const category = determineEmptyStateCategory({
      tab,
      isAuthenticated,
      locationPermissionsGranted,
      currentTabError,
      coordinatesAvailable,
    });
    return EMPTY_STATES_CONTENT[tab]?.[category] || DEFAULT_CONTENT;
  }, [
    coordinatesAvailable,
    currentTabError,
    isAuthenticated,
    locationPermissionsGranted,
    tab,
    titleOverride,
  ]);

  return (
    <ScrollView
      contentContainerStyle={fillAndCenter ? styles.scrollContent : undefined}
      style={styles.scroll}
    >
      <Box
        focusable
        style={[
          styles.wrapper,
          isWeb && !fillAndCenter && styles.wrapperWeb,
          fillAndCenter && styles.wrapperCentered,
          isDesktop && styles.wrapperDesktop,
          wrapperStyle,
        ]}
      >
        {Icon && (
          <Box
            style={[
              styles.iconWrapper,
              isLowHeightDevice ? styles.iconWrapperLowHeight : styles.iconWrapperDefaultHeight,
            ]}
          >
            <Icon />
          </Box>
        )}
        <Typography
          accessibilityLevel={1}
          accessibilityRole="header"
          color={primitive.$bbqBrown}
          focusable
          style={[styles.heading, isLowHeightDevice && styles.headingLowHeight]}
          variant="headerThree"
        >
          {titleOverride ?? t(titleId as any)}
        </Typography>
        {!titleOverride && action ? <EmptyStateAction action={action} /> : null}
      </Box>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  heading: {
    fontSize: 24,
    lineHeight: 29,
    textAlign: 'center',
  },
  headingLowHeight: {
    fontSize: 20,
    lineHeight: 24,
  },
  iconWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: spacing.$4,
    marginTop: spacing.$2,
  },
  iconWrapperDefaultHeight: {
    height: spacing.$24,
  },
  iconWrapperLowHeight: {
    height: spacing.$20,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  wrapper: {
    alignSelf: 'center',
    flex: 1,
    justifyContent: 'center',
    maxWidth: WEB_APP_CTA_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
  },
  wrapperCentered: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  wrapperDesktop: {
    paddingHorizontal: 0,
  },
  wrapperWeb: {
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 0,
  },
});
